import { useMemo, useState } from 'react'
import { useDashReport } from '../hooks/useDashReport'
import { HorizontalBarChart, FilterBar } from '../components'

interface Celda {
  especialidad: string
  provincia: string
  total: number
}

interface OpellaMedicosReport {
  titulo: string
  subtitulo: string
  total: number
  especialidades: string[]
  provincias: string[]
  celdas: Celda[]
}

interface ItemConPct {
  nombre: string
  total: number
  porcentaje: number
}

function porcentaje(parte: number, total: number): number {
  return total > 0 ? Math.round((parte / total) * 1000) / 10 : 0
}

export function OpellaMedicosEcuadorPage() {
  const { data, isLoading, error } = useDashReport<OpellaMedicosReport>('opella/medicos-ecuador')
  const [filtroEspecialidad, setFiltroEspecialidad] = useState('')
  const [filtroProvincia, setFiltroProvincia] = useState('')
  const [cruceOpen, setCruceOpen] = useState(false)

  // Celdas visibles según filtros activos
  const celdasFiltradas = useMemo(() => {
    if (!data) return []
    return data.celdas.filter(
      c =>
        (!filtroEspecialidad || c.especialidad === filtroEspecialidad) &&
        (!filtroProvincia || c.provincia === filtroProvincia)
    )
  }, [data, filtroEspecialidad, filtroProvincia])

  const resumen = useMemo(() => {
    const total = celdasFiltradas.reduce((a, c) => a + c.total, 0)

    const aggEsp = new Map<string, number>()
    const aggProv = new Map<string, number>()
    for (const c of celdasFiltradas) {
      aggEsp.set(c.especialidad, (aggEsp.get(c.especialidad) ?? 0) + c.total)
      aggProv.set(c.provincia, (aggProv.get(c.provincia) ?? 0) + c.total)
    }

    const toItems = (m: Map<string, number>): ItemConPct[] =>
      [...m.entries()]
        .map(([nombre, t]) => ({ nombre, total: t, porcentaje: porcentaje(t, total) }))
        .sort((a, b) => b.total - a.total)

    return {
      total,
      por_especialidad: toItems(aggEsp),
      por_provincia: toItems(aggProv),
    }
  }, [celdasFiltradas])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
        </div>
      </div>
    )
  }

  const especialidadPrincipal = resumen.por_especialidad[0]
  const provinciaPrincipal = resumen.por_provincia[0]
  const hayFiltro = Boolean(filtroEspecialidad || filtroProvincia)

  // Columnas de la tabla cruzada según filtro de provincia
  const columnasProv = filtroProvincia ? [filtroProvincia] : data.provincias
  const filasEsp = filtroEspecialidad ? [filtroEspecialidad] : data.especialidades
  const valorCelda = (esp: string, prov: string) =>
    data.celdas.find(c => c.especialidad === esp && c.provincia === prov)?.total ?? 0

  const chartEspecialidades = resumen.por_especialidad.map(e => ({
    label: e.nombre,
    value: e.total,
    color: '#57a6d8',
  }))
  const chartProvincias = resumen.por_provincia.map(p => ({
    label: p.nombre,
    value: p.total,
    color: '#8d82bc',
  }))

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600 flex items-center gap-2">
                  <span>🇪🇨</span> {data.subtitulo}
                </p>
              </div>
            </div>
            <a
              href="https://enterprise.tonalstrategies.com/pricing/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              Calculador
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
              </svg>
              Filtros
            </h2>
            {hayFiltro && (
              <button
                onClick={() => { setFiltroEspecialidad(''); setFiltroProvincia('') }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Limpiar filtros
              </button>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Especialidad</p>
            <FilterBar options={data.especialidades} selected={filtroEspecialidad} onSelect={setFiltroEspecialidad} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Provincia</p>
            <FilterBar options={data.provincias} selected={filtroProvincia} onSelect={setFiltroProvincia} />
          </div>
        </div>

        {/* Estadisticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen {hayFiltro ? 'Filtrado' : 'General'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{resumen.total.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-blue-600">{resumen.por_especialidad.length}</p>
              <p className="text-sm text-gray-600 mt-1">Especialidades</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-purple-600">{resumen.por_provincia.length}</p>
              <p className="text-sm text-gray-600 mt-1">Provincias</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-2xl font-bold text-blue-600">{especialidadPrincipal?.nombre ?? '—'}</p>
              <p className="text-sm text-gray-600 mt-1">
                Especialidad Principal{especialidadPrincipal ? ` (${especialidadPrincipal.porcentaje}%)` : ''}
              </p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-2xl font-bold text-purple-600">{provinciaPrincipal?.nombre ?? '—'}</p>
              <p className="text-sm text-gray-600 mt-1">
                Provincia Principal{provinciaPrincipal ? ` (${provinciaPrincipal.porcentaje}%)` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Graficas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Distribución por Especialidad
            </h3>
            <HorizontalBarChart data={chartEspecialidades} />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Distribución por Provincia
            </h3>
            <HorizontalBarChart data={chartProvincias} />
          </div>
        </div>

        {/* Matriz cruzada Especialidad x Provincia */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setCruceOpen(o => !o)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-blue-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className="text-base lg:text-lg">Cruce Especialidad × Provincia</span>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${cruceOpen ? 'rotate-180' : ''}`}
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {cruceOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Especialidad</th>
                    {columnasProv.map(prov => (
                      <th key={prov} className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">{prov}</th>
                    ))}
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filasEsp.map(esp => {
                    const totalFila = columnasProv.reduce((a, prov) => a + valorCelda(esp, prov), 0)
                    return (
                      <tr key={esp} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{esp}</td>
                        {columnasProv.map(prov => (
                          <td key={prov} className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">
                            {valorCelda(esp, prov).toLocaleString('es-EC')}
                          </td>
                        ))}
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-blue-600">{totalFila.toLocaleString('es-EC')}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    {columnasProv.map(prov => {
                      const totalCol = filasEsp.reduce((a, esp) => a + valorCelda(esp, prov), 0)
                      return (
                        <td key={prov} className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">
                          {totalCol.toLocaleString('es-EC')}
                        </td>
                      )
                    })}
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-blue-600">{resumen.total.toLocaleString('es-EC')}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies — Base de Datos Opella Médicos Ecuador</p>
        </div>

      </div>
    </div>
  )
}
